import type { QueueItem, QueueSessionSummary } from './queueApi';
import { msToTime } from '../../utils';
import { deriveTrackTitlePrefix } from '../tracks/titlePrefix';

export type QueuePointer = {
  index: number | null;
  item: QueueItem | null;
};

export type QueueItemMetadata = {
  title: string | null;
  displayTitle: string | null;
  titlePrefix: string | null;
  artists: string | null;
  artistIds: string[];
  artistCredits: Array<{ id: string | null; name: string | null }>;
  albumName: string | null;
  recordingName: string | null;
  performerName: string | null;
  albumImageUrl: string | null;
  releaseYear: number | null;
  totalDurationMs: number | null;
  trackCount: number | null;
  recordingId: string | null;
  workId: string | null;
  workName: string | null;
  composerId: string | null;
  composerName: string | null;
  catalogKey: string | null;
  catalogNumberNormalized: string | null;
  works: Array<{
    id: string | null;
    name: string | null;
    composerId: string | null;
    composerName: string | null;
    catalogKey: string | null;
    catalogNumberNormalized: string | null;
  }>;
  durationMs: number | null;
};

export const spotifyTrackIdFromUri = (uri?: string | null): string | null => {
  if (!uri) return null;
  if (uri.startsWith('spotify:track:')) {
    const parts = uri.split(':');
    return parts.length >= 3 ? parts[2] ?? null : null;
  }
  if (/^[0-9A-Za-z]{22}$/.test(uri)) {
    return uri;
  }
  if (uri.startsWith('https://open.spotify.com/track/')) {
    const segment = uri.split('/track/')[1];
    if (!segment) return null;
    return segment.split('?')[0] ?? null;
  }
  return null;
};

export const deriveQueuePointer = (
  session: QueueSessionSummary | null,
  items: QueueItem[]
): QueuePointer => {
  if (!session || items.length === 0) {
    return { index: null, item: null };
  }

  if (session.currentItemId) {
    const byId = items.findIndex((item) => item.id === session.currentItemId);
    if (byId !== -1) {
      return { index: byId, item: items[byId] ?? null };
    }
  }

  const pointer = session.currentItemIndex;
  if (Number.isFinite(pointer) && pointer >= 0 && pointer < items.length) {
    return { index: pointer, item: items[pointer] ?? null };
  }

  return { index: null, item: null };
};

export const extractQueueItemMetadata = (item?: QueueItem | null): QueueItemMetadata => {
  if (!item) {
    return {
      title: null,
      displayTitle: null,
      titlePrefix: null,
      artists: null,
      artistIds: [],
      artistCredits: [],
      albumName: null,
      recordingName: null,
      performerName: null,
      albumImageUrl: null,
      releaseYear: null,
      totalDurationMs: null,
      trackCount: null,
      recordingId: null,
      workId: null,
      workName: null,
      composerId: null,
      composerName: null,
      catalogKey: null,
      catalogNumberNormalized: null,
      works: [],
      durationMs: null
    };
  }

  const metadata =
    item.metadata && typeof item.metadata === 'object'
      ? (item.metadata as Record<string, unknown>)
      : {};

  const getString = (value: unknown): string | null => {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  };

  const rawArtists = metadata.artists;
  let artists: string | null = null;
  if (Array.isArray(rawArtists)) {
    const names = rawArtists.filter((value): value is string => typeof value === 'string');
    if (names.length > 0) {
      artists = names.join(', ');
    }
  } else if (typeof rawArtists === 'string') {
    artists = rawArtists.trim() || null;
  }

  const toStringOrNull = (value: unknown): string | null =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
  const toNumberOrNull = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const artistIds =
    Array.isArray(metadata.artistIds) && metadata.artistIds.length > 0
      ? metadata.artistIds
          .map((value) => (typeof value === 'string' && value.trim().length > 0 ? value.trim() : null))
          .filter((value): value is string => Boolean(value))
      : [];

  const artistCredits =
    Array.isArray(metadata.artistCredits) && metadata.artistCredits.length > 0
      ? (metadata.artistCredits as unknown[])
          .map((entry) => {
            if (!entry || typeof entry !== 'object') {
              return { id: null, name: null };
            }
            const record = entry as Record<string, unknown>;
            const id = toStringOrNull(record.id);
            const name = toStringOrNull(record.name);
            return { id, name };
          })
      : [];

  const works: QueueItemMetadata['works'] = Array.isArray(metadata.works)
    ? (metadata.works as unknown[]).map((entry) => {
        if (!entry || typeof entry !== 'object') {
          return {
            id: null,
            name: null,
            composerId: null,
            composerName: null,
            catalogKey: null,
            catalogNumberNormalized: null
          };
        }
        const record = entry as Record<string, unknown>;
        return {
          id: toStringOrNull(record.id),
          name: toStringOrNull(record.name),
          composerId: toStringOrNull(record.composerId),
          composerName: toStringOrNull(record.composerName),
          catalogKey: toStringOrNull(record.catalogKey),
          catalogNumberNormalized: toStringOrNull(record.catalogNumberNormalized)
        };
      })
    : [];

  const composerName = toStringOrNull(metadata.composerName);

  if (artists && composerName) {
    const composer = composerName.toLowerCase().trim();
    const filtered = artists
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value.length > 0)
      .filter((value) => value.toLowerCase() !== composer);
    artists = filtered.length > 0 ? filtered.join(', ') : null;
  }

  return {
    title: getString(metadata.title),
    displayTitle: null,
    titlePrefix: null,
    artists,
    artistIds,
    artistCredits,
    albumName: getString(metadata.albumName),
    recordingName: getString(metadata.recordingName),
    performerName: getString(metadata.performerName),
    albumImageUrl: getString(metadata.albumImageUrl),
    releaseYear: toNumberOrNull(metadata.releaseYear),
    totalDurationMs: toNumberOrNull(metadata.totalDurationMs),
    trackCount: toNumberOrNull(metadata.trackCount),
    recordingId: getString(metadata.recordingId),
    workId: getString(metadata.workId),
    workName: getString(metadata.workName),
    composerId: getString(metadata.composerId),
    composerName,
    catalogKey: getString(metadata.catalogKey),
    catalogNumberNormalized: getString(metadata.catalogNumberNormalized),
    works,
    durationMs:
      typeof metadata.durationMs === 'number' && Number.isFinite(metadata.durationMs)
        ? metadata.durationMs
        : item.durationMs
  };
};

export const formatQueueDuration = (durationMs: number | null | undefined): string => {
  if (typeof durationMs !== 'number' || Number.isNaN(durationMs) || durationMs <= 0) {
    return '—';
  }
  return msToTime(Math.round(durationMs));
};

export type QueueListEntry = {
  id: string;
  queueIndex: number;
  position: number;
  metadata: QueueItemMetadata;
  trackId: string | null;
  isCurrent: boolean;
  isPast: boolean;
  isSameRecording: boolean;
  durationMs: number | null;
  groupType: string | null;
  groupId: string | null;
};

export const buildQueueListEntries = (
  items: QueueItem[],
  pointerIndex: number | null
): QueueListEntry[] => {
  const activeRecordingId =
    pointerIndex != null && pointerIndex >= 0 && pointerIndex < items.length
      ? items[pointerIndex]?.groupType === 'recording'
        ? items[pointerIndex]?.groupId ?? null
        : null
      : null;

  const entries = items.map((item, index) => {
    const metadata = extractQueueItemMetadata(item);
    const trackId = spotifyTrackIdFromUri(item.trackUri);
    const isCurrent = pointerIndex === index;
    const isPast = pointerIndex != null && index < pointerIndex;
    const isSameRecording =
      activeRecordingId && item.groupType === 'recording' && item.groupId === activeRecordingId;

    return {
      id: item.id ?? trackId ?? `queue-item-${index}`,
      queueIndex: index,
      position: index + 1,
      metadata,
      trackId,
      isCurrent,
      isPast,
      isSameRecording: Boolean(isSameRecording),
      durationMs: typeof item.durationMs === 'number' ? item.durationMs : metadata.durationMs,
      groupType: item.groupType ?? null,
      groupId: item.groupId ?? null
    };
  });

  const recordingGroups = new Map<
    string,
    {
      indices: number[];
      titles: string[];
    }
  >();

  entries.forEach((entry, index) => {
    const title = entry.metadata.title;
    if (!title) {
      return;
    }
    let groupKey: string | null = null;
    if (entry.groupType === 'recording' && entry.groupId) {
      groupKey = entry.groupId;
    } else if (entry.metadata.recordingId) {
      groupKey = entry.metadata.recordingId;
    }
    if (!groupKey) {
      return;
    }
    const group = recordingGroups.get(groupKey);
    if (group) {
      group.indices.push(index);
      group.titles.push(title);
    } else {
      recordingGroups.set(groupKey, { indices: [index], titles: [title] });
    }
  });

  recordingGroups.forEach((group) => {
    if (!group.indices.length) return;
    const result = deriveTrackTitlePrefix(group.titles);
    if (!result.prefix) {
      return;
    }
    group.indices.forEach((entryIndex, localIndex) => {
      const entry = entries[entryIndex];
      if (!entry) return;
      const item = result.items[localIndex];
      if (!item) return;
      if (item.display !== item.original) {
        entry.metadata.displayTitle = item.display;
      }
      entry.metadata.titlePrefix = result.prefix;
    });
  });

  return entries;
};
